// MyGaz's side of « Voir le compte », redesigned after Ruben's review on
// 2 Nov 2026 (« black and white … not easy to dissociate the information »):
// the margin before the pause as a gauge with the three terms beside it, the
// week as tiles that each carry their own small picture, and the adjustment
// tucked behind one button until it is needed.
import { useState } from 'react';
import { S } from '../../lib/strings';
import { dayLabel, fmtN, xaf } from '../../lib/format';
import { blocked, rating, runwayWords, signedXaf } from '../../lib/agences';
import type { AgAgency } from '../../lib/types';
import { Ic, type IconName } from '../Icon';
import { MiniBars, Net, Star, last7 } from './Bits';

/** Where the balance stands between the pause (−limit) and comfort: the red zone below zero, the balance as a marker. */
function Gauge({ balance, limit }: { balance: number; limit: number }) {
  const right = Math.max(limit * 2, balance * 1.15, 1000);
  const span = right + limit;
  const p = (v: number) => Math.max(0, Math.min(100, ((v + limit) / span) * 100));
  const neg = balance < 0, z = p(0), b = p(balance);
  return (
    <div className="ag-gauge" role="img" aria-label={`${signedXaf(balance)} · −${xaf(limit)}`}>
      <div className="ag-gtrack">
        <span className="ag-gred" style={{ width: z + '%' }} />
        {/* The balance grows from zero: blue to the right, red into the overdraft. */}
        <span className={'ag-gfill' + (neg ? ' neg' : '')} style={neg ? { left: b + '%', width: z - b + '%' } : { left: z + '%', width: b - z + '%' }} />
        <span className="ag-gzero" style={{ left: z + '%' }} />
        <span className={'ag-gmark' + (neg ? ' neg' : '')} style={{ left: b + '%' }} />
      </div>
      <div className="ag-gscale">
        <span className="ag-gl red" style={{ left: 0 }}>{S.ag.acct.gPause} −{fmtN(limit)}</span>
        {z > 18 && <span className="ag-gl" style={{ left: z + '%' }}>0</span>}
      </div>
    </div>
  );
}

export function MarginCard({ balance, limit, feePct, payDays, feePerDay }: { balance: number; limit: number; feePct: number; payDays: number; feePerDay: number }) {
  const margin = balance + limit;
  // How many days of fees the margin buys: that is what says tight or wide, not the amount.
  const days = feePerDay > 0 ? margin / feePerDay : null;
  const [tone, word] = margin <= 0 ? ['red', S.ag.acc.paused] : days != null && days <= 3 ? ['amber', S.ag.acct.tight]
    : days != null && days <= 10 ? ['blue', S.ag.acct.fair] : ['green', S.ag.acct.safe];
  return (
    <div className="card ag-mgc">
      <div className="ag-row"><h3>{S.ag.acct.margin}</h3><span className={'st ' + tone}>{word}</span></div>
      <div className="ag-mgv num">{signedXaf(margin).replace(' XAF', '')}<small>XAF</small></div>
      <p className="muted ag-small">{days != null && margin > 0 ? S.ag.acct.marginDays(runwayWords(days)) + ' ' : ''}{S.ag.acct.marginSub(xaf(limit))}</p>
      <Gauge balance={balance} limit={limit} />
      <div className="ag-terms3">
        <div><span className="ag-tic blue"><Ic n="wallet" s="xs" /></span><b>{feePct} %</b><span>{S.ag.acct.tFee}</span></div>
        <div><span className="ag-tic red"><Ic n="lock" s="xs" /></span><b className="num">−{fmtN(limit)}</b><span>{S.ag.acct.tLimit}</span></div>
        <div><span className="ag-tic amber"><Ic n="clock" s="xs" /></span><b>{S.ag.acct.nDays(payDays)}</b><span>{S.ag.acct.tDays}</span></div>
      </div>
      <p className="ag-note2"><Ic n="eye" s="xs" />{S.ag.acct.hidden}</p>
    </div>
  );
}

function Tile({ icon, tone, label, value, sub, children }: { icon: IconName | 'star'; tone: string; label: string; value: React.ReactNode; sub?: string; children?: React.ReactNode }) {
  return (
    <div className="ag-tile">
      <div className="ag-tth"><span className={'ag-tic ' + tone}>{icon === 'star' ? <Star s={12} /> : <Ic n={icon} s="xs" />}</span><span className="cap">{label}</span></div>
      <b className="ag-tv num">{value}</b>
      {children ?? (sub ? <span className="muted ag-ts">{sub}</span> : null)}
    </div>
  );
}

/** n small segments, the first `ok` filled: « 5 sur 6 » seen at once. */
const Segments = ({ n, ok }: { n: number; ok: number }) => (
  <span className="ag-segs" aria-hidden="true">{Array.from({ length: Math.max(1, n) }, (_, i) => <i key={i} className={i < ok ? 'on' : ''} />)}</span>
);

export function WeekTiles({ a }: { a: AgAgency }) {
  const active = a.riders.filter((r) => r.state === 'active');
  const complete = active.filter((r) => !blocked(r)).length;
  const crew = a.riders.filter((r) => r.state !== 'suspended');
  const live = crew.filter((r) => r.live).length;
  const perDay = Array.from({ length: 7 }, (_, i) => a.riders.reduce((s, r) => s + (r.week[i] ?? 0), 0));
  const last = a.topups.find((x) => x.status === 'completed');
  return (
    <div className="card ag-wk">
      <div className="ag-row"><h3>{S.ag.bul.weekTitle}</h3><span className="muted ag-small">{S.ag.bul.week(dayLabel(new Date(Date.now() - 6 * 86400000)), dayLabel(new Date()))}</span></div>
      <div className="ag-tiles">
        <Tile icon="moto" tone="blue" label={S.ag.acct.wDeliv} value={fmtN(a.week.delivered)}><MiniBars vals={perDay} labels={last7()} /></Tile>
        <Tile icon="star" tone="amber" label={S.ag.acct.wRating} value={a.reviews.rating != null ? rating(a.reviews.rating) : '—'} sub={S.ag.bul.reviews(a.reviews.n)} />
        <Tile icon="clock" tone="violet" label={S.ag.acct.wTime} value={a.week.avg_min != null ? <>{a.week.avg_min}<small> min</small></> : '—'} sub={S.ag.acct.wTimeSub} />
        <Tile icon="alert" tone={a.week.cancelled >= 3 ? 'red' : 'grey'} label={S.ag.acct.wCanc} value={a.week.cancelled} sub={a.week.cancelled ? S.ag.acct.wCancSub : S.ag.acct.wCancNone} />
        <Tile icon="doc" tone={complete < active.length ? 'amber' : 'green'} label={S.ag.acct.wPapers} value={<>{complete}<small> / {active.length}</small></>}><Segments n={active.length} ok={complete} /></Tile>
        <Tile icon="users" tone="green" label={S.ag.acct.wOnline} value={<>{live}<small> / {crew.length}</small></>}><Segments n={crew.length} ok={live} /></Tile>
      </div>
      <div className="ag-lastt">
        {last ? <><Net p={last.provider} s={26} /><div><b className="num">+{xaf(last.amount)}</b><span className="muted">{S.ag.bul.lastTop} · {dayLabel(last.at, { weekday: 'short', day: 'numeric', month: 'short' })}</span></div></>
          : <span className="muted">{S.ag.tbl.noTopup}</span>}
      </div>
    </div>
  );
}

/** The adjustment, out of the way until someone needs it; quick amounts and the balance after, before saving. */
export function AdjustCard({ balance, onAdjust }: { balance: number; onAdjust(amount: number, note: string): Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const n = Math.round(Number(amount.replace(/[\s\u00a0]/g, '').replace('−', '-')));
  const valid = Number.isFinite(n) && n !== 0 && Math.abs(n) <= 500000 && note.trim().length > 2;
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) { setMsg({ ok: false, text: S.ag.acct.adjBad }); return; }
    setBusy(true); setMsg(null);
    try { await onAdjust(n, note.trim()); setAmount(''); setNote(''); setMsg({ ok: true, text: S.ag.acct.adjDone }); setOpen(false); }
    catch (err) { setMsg({ ok: false, text: String((err as Error)?.message ?? err) }); }
    finally { setBusy(false); }
  };
  if (!open) {
    return (
      <div className="card ag-adjc">
        <div><b>{S.ag.acct.adjust}</b><span className="muted ag-small">{msg?.ok ? msg.text : S.ag.acct.adjWhy}</span></div>
        <button className="btn sm" onClick={() => { setOpen(true); setMsg(null); }}><Ic n="plus" s="xs" />{S.ag.acct.adjOpen}</button>
      </div>
    );
  }
  return (
    <form className="card ag-adjf" onSubmit={save}>
      <div className="ag-row"><b>{S.ag.acct.adjust}</b><button type="button" className="ibtn" onClick={() => setOpen(false)} aria-label={S.ag.att.close}><Ic n="x" s="xs" /></button></div>
      <div className="ag-chipsq">{[1000, 5000, -500, -1000].map((q) => <button type="button" key={q} className={'chip' + (n === q ? ' on' : '')} onClick={() => setAmount(String(q))}>{q > 0 ? '+' : '−'}{fmtN(Math.abs(q))}</button>)}</div>
      <input inputMode="numeric" placeholder={S.ag.acct.adjAmount} value={amount} onChange={(e) => setAmount(e.target.value)} aria-label={S.ag.acct.adjAmount} autoFocus />
      <input placeholder={S.ag.acct.adjNote} value={note} onChange={(e) => setNote(e.target.value)} aria-label={S.ag.acct.adjNote} maxLength={200} />
      <div className="ag-after"><span className="muted">{S.ag.acct.after}</span><b className="num">{Number.isFinite(n) && n ? signedXaf(balance + n) : '—'}</b></div>
      <button className="btn sm pri" disabled={busy || !valid} aria-busy={busy}>{S.ag.acct.adjSave}</button>
      {msg && !msg.ok && <p className="ag-msg bad" role="status">{msg.text}</p>}
    </form>
  );
}
